import { useCallback, useEffect, useRef, useState } from "react";
import { Highlighter, X } from "lucide-react";

/**
 * HighlightableText — select text with the cursor to highlight it in yellow,
 * click a highlight to remove it. Session-only (resets on unmount) exactly
 * like the real MCAT interface. Preserves `\n\n` paragraph breaks.
 *
 * Props:
 *   - text: string                          the passage body
 *   - storageKey?: string                   persist across refreshes (opt-in)
 *   - color?: string                        background color (default MCAT yellow)
 *   - testid?: string                       data-testid for the wrapper
 *
 * Highlights are stored as plain {start, end} offsets into `text` so they
 * survive paragraph splitting and don't interfere with any styles.
 */
const readStored = (key) => {
  if (!key) return [];
  try {
    const raw = localStorage.getItem(`pm_hl_${key}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

export default function HighlightableText({
  text = "",
  storageKey = null,
  color = "#fff176",
  testid = "highlightable-text",
}) {
  const [highlights, setHighlights] = useState(() => readStored(storageKey));
  // Which (storageKey,text) the current `highlights` state belongs to — prevents
  // writing one passage's highlights under another passage's key mid-switch.
  const ownerRef = useRef(`${storageKey}|${text}`);
  const wrapperRef = useRef(null);
  const [toolbar, setToolbar] = useState(null); // {x, y, start, end}

  // Persist to localStorage when requested by the caller. Declared BEFORE the reload
  // effect so that on a key switch it still sees the old owner and skips the stale write.
  useEffect(() => {
    if (!storageKey || ownerRef.current !== `${storageKey}|${text}`) return;
    try { localStorage.setItem(`pm_hl_${storageKey}`, JSON.stringify(highlights)); }
    catch { /* ignore quota */ }
  }, [highlights, storageKey, text]);

  // Reload (or reset) when the underlying passage / storage key changes.
  useEffect(() => {
    const owner = `${storageKey}|${text}`;
    if (ownerRef.current === owner) return;
    ownerRef.current = owner;
    setHighlights(readStored(storageKey));
    setToolbar(null);
  }, [text, storageKey]);

  // Compute character offsets by walking from an anchor through the whole text.
  // Each paragraph span is tagged with data-start so we can resolve selection
  // offsets back to the global text index.
  const resolveSelection = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
    const range = sel.getRangeAt(0);
    if (!wrapperRef.current?.contains(range.startContainer)) return null;
    if (!wrapperRef.current?.contains(range.endContainer)) return null;

    const resolveNode = (node, offset) => {
      // Walk up until we find a span with data-start attribute.
      let n = node;
      if (n.nodeType === Node.TEXT_NODE) n = n.parentNode;
      // Climb while there's no data-start.
      while (n && n !== wrapperRef.current && !n.getAttribute?.("data-start")) {
        n = n.parentNode;
      }
      if (!n || n === wrapperRef.current) return null;
      const base = Number(n.getAttribute("data-start"));
      // Walk prior text within this span to compute offset within the span.
      const walker = document.createTreeWalker(n, NodeFilter.SHOW_TEXT);
      let localOffset = 0;
      let cur;
      while ((cur = walker.nextNode())) {
        if (cur === node) {
          return base + localOffset + offset;
        }
        localOffset += cur.textContent.length;
      }
      return null;
    };

    const start = resolveNode(range.startContainer, range.startOffset);
    const end = resolveNode(range.endContainer, range.endOffset);
    if (start == null || end == null || start === end) return null;
    const s = Math.min(start, end);
    const e = Math.max(start, end);
    const rect = range.getBoundingClientRect();
    const wrapRect = wrapperRef.current.getBoundingClientRect();
    return {
      start: s,
      end: e,
      x: rect.left + rect.width / 2 - wrapRect.left,
      y: rect.top - wrapRect.top - 8,
    };
  }, []);

  const onMouseUp = () => {
    // Defer: on mobile the selection isn't finalized until next tick.
    setTimeout(() => {
      const info = resolveSelection();
      if (!info) { setToolbar(null); return; }
      setToolbar(info);
    }, 10);
  };

  const commitHighlight = () => {
    if (!toolbar) return;
    const { start, end } = toolbar;
    setHighlights((prev) => mergeHighlight(prev, { start, end }));
    window.getSelection()?.removeAllRanges();
    setToolbar(null);
  };

  const removeHighlight = (h) => {
    setHighlights((prev) => prev.filter((x) => !(x.start === h.start && x.end === h.end)));
  };

  const clearAll = () => {
    setHighlights([]);
    setToolbar(null);
  };

  // Split text into paragraphs on \n\n, keep character offsets intact.
  const paragraphs = splitParagraphsWithOffsets(text);

  return (
    <div
      ref={wrapperRef}
      className="relative select-text"
      onMouseUp={onMouseUp}
      onTouchEnd={onMouseUp}
      data-testid={testid}
    >
      {paragraphs.map((p) => (
        <p
          key={p.start}
          data-start={p.start}
          className="text-base leading-relaxed mb-4 last:mb-0"
        >
          {renderWithHighlights(p.text, p.start, highlights, color, removeHighlight)}
        </p>
      ))}

      {toolbar && (
        <button
          onClick={commitHighlight}
          onMouseDown={(e) => e.preventDefault()}
          className="absolute z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold shadow-lg"
          style={{
            left: Math.max(10, Math.min((wrapperRef.current?.clientWidth || 400) - 110, toolbar.x - 50)),
            top: Math.max(0, toolbar.y - 28),
            background: color,
            color: "#2a2a2a",
          }}
          data-testid="highlight-apply-btn"
        >
          <Highlighter size={12} /> Highlight
        </button>
      )}

      {highlights.length > 0 && (
        <button
          onClick={clearAll}
          className="absolute right-0 -top-10 inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100"
          style={{ color: "var(--text-muted)" }}
          data-testid="highlight-clear-all"
          title="Clear all highlights"
        >
          <X size={12} /> Clear {highlights.length} highlight{highlights.length === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

/** Merge a new highlight, collapsing overlapping ranges. */
function mergeHighlight(existing, next) {
  const all = [...existing, next].sort((a, b) => a.start - b.start);
  const merged = [];
  for (const h of all) {
    const top = merged[merged.length - 1];
    if (top && h.start <= top.end) {
      top.end = Math.max(top.end, h.end);
    } else {
      merged.push({ ...h });
    }
  }
  return merged;
}

/** Split on blank lines, preserving character offsets of each paragraph start. */
function splitParagraphsWithOffsets(text) {
  const parts = [];
  const re = /\n{2,}/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    parts.push({ start: last, text: text.slice(last, m.index) });
    last = m.index + m[0].length;
  }
  parts.push({ start: last, text: text.slice(last) });
  return parts;
}

/** Render a single paragraph with any overlapping highlights applied. */
function renderWithHighlights(paraText, paraStart, highlights, color, onRemove) {
  const paraEnd = paraStart + paraText.length;
  // Only highlights that touch this paragraph.
  const hits = highlights
    .filter((h) => h.start < paraEnd && h.end > paraStart)
    .sort((a, b) => a.start - b.start);
  if (hits.length === 0) return paraText;

  const out = [];
  let cursor = paraStart;
  hits.forEach((h, i) => {
    const s = Math.max(h.start, paraStart);
    const e = Math.min(h.end, paraEnd);
    if (cursor < s) {
      out.push(<span key={`t${cursor}`}>{paraText.slice(cursor - paraStart, s - paraStart)}</span>);
    }
    out.push(
      <mark
        key={`h${i}-${s}`}
        onClick={() => onRemove(h)}
        className="cursor-pointer rounded px-0.5"
        style={{ background: color, color: "#2a2a2a" }}
        title="Click to remove highlight"
      >
        {paraText.slice(s - paraStart, e - paraStart)}
      </mark>
    );
    cursor = e;
  });
  if (cursor < paraEnd) {
    out.push(<span key={`t${cursor}-end`}>{paraText.slice(cursor - paraStart)}</span>);
  }
  return out;
}
